import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import * as XLSX from "xlsx";
import PageHeader from "../components/layout/PageHeader";
import KpiCard from "../components/ui/KpiCard";
import ChartCard from "../components/ui/ChartCard";
import StatusBadge from "../components/ui/StatusBadge";
import ProgressBar from "../components/ui/ProgressBar";
import { EmptyState, ErrorState, KpiSkeletonRow } from "../components/ui/states";
import { useApi } from "../lib/useApi";
import { api, qs, ApiClientError } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/ui/Toast";
import { formatUSD, formatPct, formatDate, signClass } from "../lib/format";
import { CHART } from "../lib/chart";
import { SETTLED, STATUS_OPTIONS, STATUS_COLORS, classify, isFinancing } from "../lib/recon";
import type {
  Project,
  ReconciliationResponse,
  ReconciliationItem,
  ReconExportResponse,
  SovSpendResponse,
  SovCategorySpend,
} from "@shared/types";

export default function Reconciliation() {
  const { activeClientId, isBtzl } = useAuth();
  const toast = useToast();
  const params = useParams();

  const recon = useApi<ReconciliationResponse>(`/api/reconciliation${qs({ clientId: activeClientId })}`);
  const projectsState = useApi<{ projects: Project[] }>(`/api/projects${qs({ clientId: activeClientId })}`);
  const sovSpend = useApi<SovSpendResponse>(`/api/portfolio/sov${qs({ clientId: activeClientId })}`);

  const [items, setItems] = useState<ReconciliationItem[]>([]);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [projectFilter, setProjectFilter] = useState<string>(params.projectId ?? "all");
  const [sovProject, setSovProject] = useState<string>("all");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (recon.data) setItems(recon.data.items);
  }, [recon.data]);

  const categories = recon.data?.categories ?? [];
  const projects = projectsState.data?.projects ?? [];
  const canEdit = recon.data?.canEdit ?? false;

  async function patchItem(item: ReconciliationItem, patch: Record<string, unknown>) {
    setSavingId(item.id);
    const prev = items;
    setItems((cur) => cur.map((i) => (i.id === item.id ? { ...i, ...patch } : i)));
    try {
      const { item: updated } = await api.patch<{ item: ReconciliationItem }>(
        `/api/reconciliation/${item.id}`,
        patch
      );
      setItems((cur) => cur.map((i) => (i.id === item.id ? updated : i)));
      recon.refetch(); // refresh aggregates
    } catch (err) {
      setItems(prev);
      toast.error(err instanceof ApiClientError ? err.message : "Could not update movement");
    } finally {
      setSavingId(null);
    }
  }

  function matchesProject(i: ReconciliationItem): boolean {
    if (projectFilter === "all") return true;
    if (projectFilter === "unassigned") return i.projectId == null;
    return String(i.projectId) === projectFilter;
  }

  const filtered = useMemo(() => items.filter(matchesProject), [items, projectFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  const inbox = useMemo(() => filtered.filter((i) => !SETTLED.has(i.matchStatus)), [filtered]);

  // Aggregates are computed from the *filtered* set so the KPIs and charts track
  // the project selector (overall, a single project, or needs-assignment).
  const agg = useMemo(() => {
    const byStatus = new Map<string, { count: number; amount: number }>();
    const byCat = new Map<string, { reconciled: number; financing: number; review: number; total: number; count: number }>();
    let reconciled = 0;
    let financing = 0;
    let review = 0;
    let needsReview = 0;
    let financingCount = 0;
    for (const it of filtered) {
      const amt = Math.abs(it.amount ?? 0);
      const cls = classify(it.matchStatus);
      if (cls === "reconciled") reconciled += amt;
      else if (cls === "financing") {
        financing += amt;
        financingCount += 1;
      } else {
        review += amt;
        needsReview += 1;
      }
      const s = byStatus.get(it.matchStatus) ?? { count: 0, amount: 0 };
      s.count += 1;
      s.amount += amt;
      byStatus.set(it.matchStatus, s);
      const cat = it.sovStage || "Unassigned";
      const c = byCat.get(cat) ?? { reconciled: 0, financing: 0, review: 0, total: 0, count: 0 };
      c[cls] += amt;
      c.total += amt;
      c.count += 1;
      byCat.set(cat, c);
    }
    return {
      totals: { reconciled, financing, review, total: reconciled + financing + review },
      byStatus: [...byStatus.entries()].map(([matchStatus, v]) => ({ matchStatus, ...v })),
      byCategory: [...byCat.entries()].map(([category, v]) => ({ category, ...v })).sort((a, b) => b.review - a.review),
      needsReview,
      financingCount,
    };
  }, [filtered]);

  const financingRows = useMemo(() => filtered.filter((i) => isFinancing(i.matchStatus)), [filtered]);

  async function exportExcel() {
    setExporting(true);
    try {
      const data = await api.get<ReconExportResponse>(`/api/export/reconciliation${qs({ clientId: activeClientId })}`);
      const current = data.rows.map((r) => ({
        Ref: r.rowRef,
        Date: r.date ?? "",
        Week: r.weekLabel ?? "",
        Counterparty: r.counterparty ?? "",
        Type: r.movementType,
        Amount: r.amount,
        "Cash Impact": r.cashImpact,
        Project: r.current.project ?? "",
        Category: r.current.sovStage ?? "",
        Status: r.current.matchStatus ?? "",
      }));
      const changes = data.rows
        .filter((r) => r.changed)
        .map((r) => ({
          Ref: r.rowRef,
          Date: r.date ?? "",
          Counterparty: r.counterparty ?? "",
          "Project (original)": r.original.project ?? "—",
          "Project (current)": r.current.project ?? "—",
          "Category (original)": r.original.sovStage ?? "—",
          "Category (current)": r.current.sovStage ?? "—",
          "Status (original)": r.original.matchStatus ?? "—",
          "Status (current)": r.current.matchStatus ?? "—",
          "Edited by": r.editedByEmail ?? "",
          "Edited at": r.editedAt ?? "",
        }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(current), "Ledger (current)");
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(changes.length ? changes : [{ Note: "No changes vs original yet" }]),
        "Changes vs original"
      );
      XLSX.writeFile(wb, `Gable_Homes_Reconciliation_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`Exported ${data.rows.length} movements (${changes.length} changed)`);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not export");
    } finally {
      setExporting(false);
    }
  }

  if (projectsState.loading || recon.loading) {
    return (
      <>
        <PageHeader title="Reconciliation" />
        <KpiSkeletonRow count={3} />
      </>
    );
  }
  if (recon.error) {
    return (
      <>
        <PageHeader title="Reconciliation" />
        <ErrorState message={recon.error} onRetry={recon.refetch} />
      </>
    );
  }

  const t = agg.totals;
  const recPct = t.total > 0 ? t.reconciled / t.total : 0;
  const finPct = t.total > 0 ? t.financing / t.total : 0;
  const revPct = t.total > 0 ? t.review / t.total : 0;
  const pieData = agg.byStatus.map((s) => ({ name: s.matchStatus, value: s.amount }));

  return (
    <>
      <PageHeader
        title="Reconciliation"
        subtitle={
          canEdit
            ? "Assign each movement to a project and SOV category, then mark it reconciled."
            : "Read-only view of the reconciliation ledger."
        }
        actions={
          <div className="flex items-center gap-2">
            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className="rounded-lg border border-ink-300 bg-white px-3 py-1.5 text-sm focus-ring"
            >
              <option value="all">All projects</option>
              <option value="unassigned">Needs assignment</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            {isBtzl && (
              <button onClick={exportExcel} disabled={exporting} className="btn-primary">
                {exporting ? "Exporting…" : "Export Excel"}
              </button>
            )}
          </div>
        }
      />

      {items.length === 0 ? (
        <EmptyState
          title="No reconciliation data"
          message="No bank movements have been ingested yet."
          icon="⇄"
        />
      ) : (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiCard label="Reconciled" value={formatUSD(t.reconciled, { compact: true })} accent="positive" sub={`${formatPct(recPct)} of total`} />
            <KpiCard label="Financing (no receipt)" value={formatUSD(t.financing, { compact: true })} sub={`${agg.financingCount} financial movement${agg.financingCount === 1 ? "" : "s"}`} />
            <KpiCard label="Needs review" value={formatUSD(t.review, { compact: true })} accent="warn" sub={`${agg.needsReview} to reconcile`} />
            <KpiCard label="Total movements" value={formatUSD(t.total, { compact: true })} sub={`${filtered.length} movement${filtered.length === 1 ? "" : "s"}`} />
          </div>

          {/* Split bar + pie */}
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <ChartCard title="Reconciliation status">
              <div className="flex h-8 w-full overflow-hidden rounded-lg bg-ink-100">
                <div className="flex items-center justify-center bg-positive text-xs font-semibold text-white" style={{ width: `${recPct * 100}%` }}>
                  {recPct > 0.12 ? formatPct(recPct) : ""}
                </div>
                <div className="flex items-center justify-center text-xs font-semibold text-white" style={{ width: `${finPct * 100}%`, backgroundColor: CHART.axis }}>
                  {finPct > 0.12 ? formatPct(finPct) : ""}
                </div>
                <div className="flex items-center justify-center bg-warn text-xs font-semibold text-white" style={{ width: `${revPct * 100}%` }}>
                  {revPct > 0.12 ? formatPct(revPct) : ""}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-4 text-xs">
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-positive" /> Reconciled {formatUSD(t.reconciled)}</span>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: CHART.axis }} /> Financing {formatUSD(t.financing)}</span>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-warn" /> Needs review {formatUSD(t.review)}</span>
              </div>
            </ChartCard>

            <ChartCard title="By match status">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                    {pieData.map((d) => <Cell key={d.name} fill={STATUS_COLORS[d.name] ?? CHART.axis} />)}
                  </Pie>
                  <Tooltip formatter={(v: number) => formatUSD(v)} />
                  <Legend wrapperStyle={CHART.legend} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Spend by SOV category — budget vs actual from the canonical Budget SOV sheet */}
          <ChartCard
            title="Spend by SOV category"
            subtitle="Budget vs actual-to-date from the Budget SOV sheet (all projects)"
            className="mt-4"
          >
            {sovSpend.loading ? (
              <div className="skeleton h-40 w-full" />
            ) : (sovSpend.data?.byCategory.length ?? 0) === 0 ? (
              <EmptyState title="No Budget SOV data" message="No schedule-of-values budget has been ingested." icon="▦" />
            ) : (
              <SovBudgetTable rows={sovSpend.data!.byCategory} />
            )}
          </ChartCard>

          {/* Per-project SOV budget vs spend */}
          <ChartCard
            title="SOV budget vs spend by project"
            subtitle="Pick a project to see its budget and actual spend per category"
            className="mt-4"
            actions={
              (sovSpend.data?.byProject.length ?? 0) > 0 && (
                <select
                  value={sovProject}
                  onChange={(e) => setSovProject(e.target.value)}
                  className="rounded-lg border border-ink-300 bg-white px-3 py-1.5 text-sm focus-ring"
                >
                  <option value="all">All projects</option>
                  {sovSpend.data!.byProject.map((p) => (
                    <option key={p.projectId} value={p.projectId}>
                      {p.projectName}
                    </option>
                  ))}
                </select>
              )
            }
          >
            {sovSpend.loading ? (
              <div className="skeleton h-40 w-full" />
            ) : (sovSpend.data?.byProject.length ?? 0) === 0 ? (
              <EmptyState title="No projects" message="No Budget SOV data by project." icon="▦" />
            ) : (
              <div className="space-y-5">
                {sovSpend
                  .data!.byProject.filter((p) => sovProject === "all" || String(p.projectId) === sovProject)
                  .map((p) => (
                    <div key={p.projectId}>
                      <div className="mb-1.5 flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-ink-800">{p.projectName}</h4>
                        <span className="text-xs tabular-nums text-ink-500">
                          {formatUSD(p.actual)} spent / {formatUSD(p.budget)} budget
                        </span>
                      </div>
                      <SovBudgetTable rows={p.lines} />
                    </div>
                  ))}
              </div>
            )}
          </ChartCard>

          {/* Financial movements (intentionally not reconciled — financing, no receipt) */}
          {financingRows.length > 0 && (
            <div className="mt-4">
              <h3 className="mb-1 text-sm font-semibold text-ink-800">
                Financial movements ({financingRows.length})
              </h3>
              <p className="mb-2 text-xs text-ink-500">
                Financing movements that intentionally have no receipt. They are settled by
                design and are not pending reconciliation.
              </p>
              <MovementTable
                rows={financingRows}
                categories={categories}
                projects={projects}
                canEdit={canEdit}
                isBtzl={isBtzl}
                savingId={savingId}
                onPatch={patchItem}
              />
            </div>
          )}

          {/* Needs review inbox */}
          {inbox.length > 0 && (
            <div className="mt-4 rounded-xl border border-warn/30 bg-warn-50 p-4">
              <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-warn-700">
                <span className="h-2 w-2 rounded-full bg-warn" /> Needs review ({inbox.length})
              </h3>
              <p className="mb-3 text-xs text-ink-500">
                {canEdit
                  ? "Assign a project and SOV category from each receipt, then reconcile."
                  : "These movements are awaiting reconciliation."}
              </p>
              <MovementTable
                rows={inbox}
                categories={categories}
                projects={projects}
                canEdit={canEdit}
                isBtzl={isBtzl}
                savingId={savingId}
                onPatch={patchItem}
                showReconcile
              />
            </div>
          )}

          {/* All movements */}
          <div className="mt-4">
            <h3 className="mb-2 text-sm font-semibold text-ink-800">All movements ({filtered.length})</h3>
            <MovementTable
              rows={filtered}
              categories={categories}
              projects={projects}
              canEdit={canEdit}
              isBtzl={isBtzl}
              savingId={savingId}
              onPatch={patchItem}
            />
          </div>
        </>
      )}
    </>
  );
}

/* ----------------------------- SOV budget table --------------------------- */

function SovBudgetTable({ rows }: { rows: SovCategorySpend[] }) {
  if (rows.length === 0) {
    return <p className="rounded-lg border border-ink-200 bg-white px-4 py-6 text-center text-sm text-ink-400">No categories.</p>;
  }
  return (
    <div className="space-y-2">
      {rows.map((c) => {
        const ratio = c.budget > 0 ? c.actual / c.budget : 0;
        return (
          <div key={c.category} className="grid grid-cols-[10rem,1fr,auto] items-center gap-3">
            <span className="truncate text-sm font-medium text-ink-800">{c.category}</span>
            <div className="w-full">
              <ProgressBar ratio={ratio} />
            </div>
            <span className="w-44 text-right text-xs tabular-nums text-ink-500">
              {formatUSD(c.actual)} / {formatUSD(c.budget)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------- movement table --------------------------- */

function MovementTable({
  rows,
  categories,
  projects,
  canEdit,
  isBtzl,
  savingId,
  onPatch,
  showReconcile = false,
}: {
  rows: ReconciliationItem[];
  categories: string[];
  projects: Project[];
  canEdit: boolean;
  isBtzl: boolean;
  savingId: number | null;
  onPatch: (item: ReconciliationItem, patch: Record<string, unknown>) => void;
  showReconcile?: boolean;
}) {
  if (rows.length === 0) {
    return <p className="rounded-lg border border-ink-200 bg-white px-4 py-6 text-center text-sm text-ink-400">Nothing here.</p>;
  }
  return (
    <div className="max-h-[34rem] overflow-auto rounded-lg border border-ink-200">
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-10 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
          <tr>
            <th className="px-3 py-2 font-medium">Date</th>
            <th className="px-3 py-2 font-medium">Counterparty / receipt</th>
            <th className="px-3 py-2 font-medium">Project</th>
            <th className="px-3 py-2 font-medium">Category</th>
            <th className="px-3 py-2 text-right font-medium">Amount</th>
            <th className="px-3 py-2 font-medium">Status</th>
            {isBtzl && <th className="px-3 py-2 font-medium">Edited by</th>}
            {showReconcile && <th className="px-3 py-2" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100 bg-white">
          {rows.map((item) => {
            const busy = savingId === item.id;
            return (
              <tr key={item.id} className="align-top">
                <td className="whitespace-nowrap px-3 py-2 text-ink-600">{formatDate(item.date)}</td>
                <td className="px-3 py-2">
                  <div className="font-medium text-ink-900">{item.counterparty ?? "—"}</div>
                  <div className="text-xs text-ink-400">
                    {item.movementType}
                    {item.receiptId ? ` · ${item.receiptId}` : " · no receipt"}
                  </div>
                  {item.items && <div className="mt-0.5 max-w-md text-xs text-ink-400">{item.items}</div>}
                </td>
                <td className="px-3 py-2">
                  {canEdit ? (
                    <select
                      value={item.projectId ?? ""}
                      disabled={busy}
                      onChange={(e) => onPatch(item, { projectId: e.target.value ? Number(e.target.value) : null })}
                      className="w-36 rounded-md border border-ink-300 bg-white px-2 py-1 text-xs focus-ring disabled:opacity-50"
                    >
                      <option value="">Needs assignment</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-ink-700">{item.projectName ?? "—"}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {canEdit ? (
                    <select
                      value={item.sovStage ?? ""}
                      disabled={busy}
                      onChange={(e) => onPatch(item, { sovStage: e.target.value || null })}
                      className="w-36 rounded-md border border-ink-300 bg-white px-2 py-1 text-xs focus-ring disabled:opacity-50"
                    >
                      <option value="">Uncategorized</option>
                      {categories.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-ink-700">{item.sovStage ?? "—"}</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                  <span className={signClass(item.cashImpact)}>{formatUSD(item.amount)}</span>
                </td>
                <td className="px-3 py-2">
                  {canEdit ? (
                    <select
                      value={item.matchStatus}
                      disabled={busy}
                      onChange={(e) => onPatch(item, { matchStatus: e.target.value })}
                      className="rounded-md border border-ink-300 bg-white px-2 py-1 text-xs focus-ring disabled:opacity-50"
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <StatusBadge status={item.matchStatus} />
                  )}
                </td>
                {isBtzl && (
                  <td className="px-3 py-2 text-xs text-ink-500">
                    {item.updatedByEmail ? (
                      <span title={item.updatedAt ?? ""}>{item.updatedByEmail}</span>
                    ) : (
                      <span className="text-ink-300">—</span>
                    )}
                  </td>
                )}
                {showReconcile && (
                  <td className="px-3 py-2 text-right">
                    {canEdit && (
                      <button
                        onClick={() => onPatch(item, { matchStatus: "Reconciled" })}
                        disabled={busy}
                        className="rounded-md bg-positive px-2.5 py-1 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
                      >
                        Reconcile
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
